/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { PtlPillComponent } from '@app/app/shared/styleguide/components/pill/ptl-pill.component';
import { LucideDynamicIcon, LucideMic, LucidePause, LucidePlay } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { MediaUrlData } from '../../../models';

@Component({
  selector: 'ptl-v3-resource-audio-preview',
  standalone: true,
  imports: [CommonModule, LucideDynamicIcon, PtlPillComponent, TranslocoModule],
  templateUrl: './v3-audio-preview.component.html',
  styleUrls: ['./v3-audio-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3ResourceAudioPreviewComponent {
  /** Receives the EditorContent object */
  @Input() content!: MediaUrlData;

  @ViewChild('audioElement') private audioElementRef?: ElementRef<HTMLAudioElement>;

  isPlaying = false;
  currentTime = 0;
  duration = 0;

  protected readonly LucidePlay = LucidePlay;
  protected readonly LucidePause = LucidePause;
  protected readonly LucideMic = LucideMic;

  get progressPct(): number {
    return this.duration ? Math.min(100, (this.currentTime / this.duration) * 100) : 0;
  }

  get currentTimeLabel(): string {
    return this.formatTime(this.currentTime);
  }

  get durationLabel(): string {
    return this.formatTime(this.duration);
  }

  togglePlay(): void {
    const audio = this.audioElementRef?.nativeElement;
    if (!audio) {
      return;
    }

    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
  }

  skip(deltaSeconds: number): void {
    const audio = this.audioElementRef?.nativeElement;
    if (!audio || !this.duration) {
      return;
    }

    audio.currentTime = Math.min(this.duration, Math.max(0, audio.currentTime + deltaSeconds));
  }

  seek(event: MouseEvent): void {
    const audio = this.audioElementRef?.nativeElement;
    if (!audio || !this.duration) {
      return;
    }

    const track = event.currentTarget as HTMLElement;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));

    audio.currentTime = ratio * this.duration;
  }

  onPlay(): void {
    this.isPlaying = true;
  }

  onPause(): void {
    this.isPlaying = false;
  }

  onEnded(): void {
    this.isPlaying = false;
  }

  onTimeUpdate(): void {
    this.currentTime = this.audioElementRef?.nativeElement.currentTime ?? 0;
  }

  onLoadedMetadata(): void {
    const audioDuration = this.audioElementRef?.nativeElement.duration;
    this.duration = audioDuration && isFinite(audioDuration) ? audioDuration : 0;
  }

  private formatTime(totalSeconds: number): string {
    if (!totalSeconds || !isFinite(totalSeconds)) {
      return '0:00';
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}
